import { supabase } from '../lib/supabase';

const EDGE_FUNCTION_URL = `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-email-notifications`;

async function callEmailFunction(payload) {
  try {
    const { data: { session } } = await supabase?.auth?.getSession();
    const res = await fetch(EDGE_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session?.access_token || import.meta.env?.VITE_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify(payload),
    });
    const data = await res?.json();
    if (!res?.ok) return { data: null, error: { message: data?.error || 'Email send failed' } };
    return { data, error: null };
  } catch (err) {
    return { data: null, error: { message: err?.message || 'Network error' } };
  }
}

export const notificationService = {
  /**
   * Send invite email to a new team member.
   */
  async sendInviteEmail({ toEmail, inviteeName, inviterName, companyName, role, siteName, inviteUrl, userId }) {
    return callEmailFunction({
      type: 'invite',
      toEmail,
      inviteeName,
      inviterName,
      companyName,
      role,
      siteName,
      inviteUrl,
      userId,
    });
  },

  /**
   * Send password reset email.
   */
  async sendPasswordResetEmail({ toEmail, userName, resetUrl }) {
    return callEmailFunction({
      type: 'password_reset',
      toEmail,
      userName,
      resetUrl,
    });
  },

  /**
   * Trigger document expiry alert batch (scans all expiring docs and emails admins).
   */
  async sendDocumentExpiryAlerts() {
    return callEmailFunction({ type: 'document_expiry' });
  },

  /**
   * Send snag assigned notification.
   */
  async sendSnagAssigned({ snagId, assigneeEmail, assigneeName, assigneeUserId, assignerName, snagTitle, snagDescription, severity, siteName, dueDate }) {
    return callEmailFunction({
      type: 'snag_assigned',
      snagId,
      assigneeEmail,
      assigneeName,
      assigneeUserId,
      assignerName,
      snagTitle,
      snagDescription,
      severity,
      siteName,
      dueDate,
    });
  },

  /**
   * Send snag status changed notification to assigned user and/or creator.
   */
  async sendSnagStatusChanged({ snagId, title, site, status, assignedTo, creator }) {
    return callEmailFunction({
      type: 'snag_status_changed',
      snagId,
      snagTitle: title,
      siteName: site,
      newStatus: status,
      assignedToUserId: assignedTo || null,
      creatorUserId: creator || null,
    });
  },

  /**
   * Trigger overdue snag notifications batch.
   */
  async sendSnagOverdueAlerts() {
    return callEmailFunction({ type: 'snag_overdue' });
  },

  /**
   * Send task assigned notification.
   */
  async sendTaskAssigned({ taskId, assigneeEmail, assigneeName, assigneeUserId, assignerName, taskTitle, taskDescription, category, siteName, dueDate }) {
    return callEmailFunction({
      type: 'task_assigned',
      taskId,
      assigneeEmail,
      assigneeName,
      assigneeUserId,
      assignerName,
      taskTitle,
      taskDescription,
      category,
      siteName,
      dueDate,
    });
  },

  /**
   * Trigger overdue task notifications batch.
   */
  async sendTaskOverdueAlerts() {
    return callEmailFunction({ type: 'task_overdue' });
  },

  /**
   * Trigger weekly digest for all admin + site_manager users.
   */
  async sendWeeklyDigest() {
    return callEmailFunction({ type: 'weekly_digest' });
  },

  // ─── Notification Preferences ──────────────────────────────────────────────

  async getPreferences(userId) {
    try {
      const { data, error } = await supabase?.from('notification_preferences')?.select('*')?.eq('user_id', userId)?.maybeSingle();
      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      return { data: null, error: { message: err?.message } };
    }
  },

  async updatePreferences(userId, prefs) {
    try {
      const { data, error } = await supabase?.from('notification_preferences')?.upsert({ user_id: userId, ...prefs }, { onConflict: 'user_id' })?.select()?.single();
      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      return { data: null, error: { message: err?.message } };
    }
  },

  // ─── In-App Notification Methods ──────────────────────────────────────────

  /**
   * Create an in-app notification for a specific user.
   */
  async createNotification(userId, type, title, message, linkUrl, referenceId, siteId) {
    try {
      if (!userId) return { data: null, error: null }; // no-op if no target user
      const { data: { user } } = await supabase?.auth?.getUser();
      const { data: profile } = await supabase
        ?.from('user_profiles')
        ?.select('company_id')
        ?.eq('id', userId)
        ?.maybeSingle();

      const { data, error } = await supabase?.from('notifications')?.insert({
        user_id: userId,
        company_id: profile?.company_id || null,
        site_id: siteId || null,
        type: type || 'general',
        title,
        message,
        link_url: linkUrl || null,
        reference_id: referenceId || null,
        is_read: false,
        priority: 'medium',
      })?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      return { data: null, error: { message: err?.message } };
    }
  },

  /**
   * Get all notifications for a user, ordered by newest first.
   */
  async getUserNotifications(userId) {
    try {
      if (!userId) return { data: [], error: null };
      const { data, error } = await supabase
        ?.from('notifications')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.order('created_at', { ascending: false })
        ?.limit(50);

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (err) {
      return { data: [], error: { message: err?.message } };
    }
  },

  /**
   * Mark a single notification as read.
   */
  async markAsRead(notificationId) {
    try {
      const { data, error } = await supabase
        ?.from('notifications')
        ?.update({ is_read: true })
        ?.eq('id', notificationId)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      return { data: null, error: { message: err?.message } };
    }
  },

  /**
   * Mark all notifications as read for a user.
   */
  async markAllAsRead(userId) {
    try {
      if (!userId) return { error: null };
      const { error } = await supabase
        ?.from('notifications')
        ?.update({ is_read: true })
        ?.eq('user_id', userId)
        ?.eq('is_read', false);

      if (error) throw error;
      return { error: null };
    } catch (err) {
      return { error: { message: err?.message } };
    }
  },

  /**
   * Subscribe to real-time notifications for a user.
   * Returns the channel — caller must call supabase.removeChannel(channel) on cleanup.
   */
  subscribeToUserNotifications(userId, callback) {
    if (!userId) return null;
    const channel = supabase
      ?.channel(`notifications:${userId}`)
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          callback?.(payload?.new);
        }
      )
      ?.subscribe();
    return channel;
  },

  // ─── New Event Notification Helpers ────────────────────────────────────────

  /**
   * Notify all admin/site_manager users on a site that an incident was reported.
   */
  async notifyIncidentReported(incidentId, siteId, companyId, reportedByName) {
    try {
      if (!siteId || !companyId) return;
      const { data: users } = await supabase
        ?.from('user_profiles')
        ?.select('id')
        ?.eq('company_id', companyId)
        ?.in('role', ['admin', 'site_manager']);
      if (!users?.length) return;
      await Promise.all(
        users?.map((u) =>
          this.createNotification(
            u?.id,
            'incident_reported',
            'New Incident Reported',
            `${reportedByName || 'A team member'} reported a new incident on site.`,
            `/incident-reporting?site=${siteId}`,
            incidentId,
            siteId
          )
        )
      );
    } catch (err) {
      console.error('notifyIncidentReported error:', err?.message);
    }
  },

  /**
   * Notify all admin/site_manager users in a company that a task was marked
   * as blocked. Mirrors notifyIncidentReported's fan-out pattern.
   */
  async notifyTaskBlocked(taskId, siteId, companyId, taskTitle, blockedByName, note) {
    try {
      if (!siteId || !companyId) return;
      const { data: users } = await supabase
        ?.from('user_profiles')
        ?.select('id')
        ?.eq('company_id', companyId)
        ?.in('role', ['admin', 'site_manager']);
      if (!users?.length) return;
      const msg = `${blockedByName || 'A worker'} marked "${taskTitle || 'a task'}" as blocked.${note ? ` Reason: ${note}` : ''}`;
      await Promise.all(
        users?.map((u) =>
          this.createNotification(
            u?.id,
            'task_blocked',
            'Task Blocked',
            msg,
            `/task-completion-workflow?site=${siteId}`,
            taskId,
            siteId
          )
        )
      );
    } catch (err) {
      console.error('notifyTaskBlocked error:', err?.message);
    }
  },

  /**
   * Notify all admin/site_manager users on a site that an incident status changed.
   */
  async notifyIncidentStatusChanged(incidentId, siteId, companyId, newStatus) {
    try {
      if (!siteId || !companyId) return;
      const { data: users } = await supabase
        ?.from('user_profiles')
        ?.select('id')
        ?.eq('company_id', companyId)
        ?.in('role', ['admin', 'site_manager']);
      if (!users?.length) return;
      const statusLabel = newStatus?.replace(/_/g, ' ')?.replace(/\b\w/g, (c) => c?.toUpperCase()) || newStatus;
      await Promise.all(
        users?.map((u) =>
          this.createNotification(
            u?.id,
            'incident_status_changed',
            'Incident Status Updated',
            `An incident status has been changed to "${statusLabel}".`,
            `/incident-reporting?site=${siteId}`,
            incidentId,
            siteId
          )
        )
      );
    } catch (err) {
      console.error('notifyIncidentStatusChanged error:', err?.message);
    }
  },

  /**
   * Notify a specific worker that an H&S document has been assigned to them.
   */
  async notifyRamsAssigned(ramsId, workerUserId, title) {
    try {
      if (!workerUserId) return;
      await this.createNotification(
        workerUserId,
        'rams_assigned',
        'H&S Document Assigned',
        `You have been assigned an H&S document: "${title || 'H&S Document'}". Please review and acknowledge.`,
        `/rams-worker-view`,
        ramsId,
        null
      );
    } catch (err) {
      console.error('notifyRamsAssigned error:', err?.message);
    }
  },

  /**
   * Notify a specific worker that a site induction has been assigned to them.
   */
  async notifyInductionAssigned(inductionId, workerUserId, title) {
    try {
      if (!workerUserId) return;
      await this.createNotification(
        workerUserId,
        'induction_assigned',
        'Site Induction Assigned',
        `You have been assigned a site induction: "${title || 'Site Induction'}". Please complete it.`,
        `/site-inductions-worker-view`,
        inductionId,
        null
      );
    } catch (err) {
      console.error('notifyInductionAssigned error:', err?.message);
    }
  },

  /**
   * Notify a specific worker that a toolbox talk has been assigned to them.
   */
  async notifyToolboxTalkAssigned(talkId, workerUserId, title) {
    try {
      if (!workerUserId) return;
      await this.createNotification(
        workerUserId,
        'toolbox_talk_assigned',
        'Toolbox Talk Assigned',
        `You have been assigned a toolbox talk: "${title || 'Toolbox Talk'}". Please confirm attendance.`,
        `/toolbox-talks-worker-view`,
        talkId,
        null
      );
    } catch (err) {
      console.error('notifyToolboxTalkAssigned error:', err?.message);
    }
  },

  /**
   * Notify admin users in a company that a worker certification is expiring within 30 days.
   */
  async notifyCertExpiring(workerUserId, certType, expiryDate, companyId) {
    try {
      if (!companyId) return;
      const { data: admins } = await supabase
        ?.from('user_profiles')
        ?.select('id')
        ?.eq('company_id', companyId)
        ?.eq('role', 'admin');
      if (!admins?.length) return;
      const certLabel = certType?.replace(/_/g, ' ')?.replace(/\b\w/g, (c) => c?.toUpperCase()) || certType;
      const expiryStr = expiryDate ? new Date(expiryDate)?.toLocaleDateString('en-GB') : 'soon';
      await Promise.all(
        admins?.map((a) =>
          this.createNotification(
            a?.id,
            'cert_expiring',
            'Certification Expiring Soon',
            `A worker's ${certLabel} certification is expiring on ${expiryStr}. Please take action.`,
            `/worker-certifications`,
            workerUserId,
            null
          )
        )
      );
    } catch (err) {
      console.error('notifyCertExpiring error:', err?.message);
    }
  },

  /**
   * Notify admin users in a company that a worker certification has expired.
   */
  async notifyCertExpired(workerUserId, certType, companyId) {
    try {
      if (!companyId) return;
      const { data: admins } = await supabase
        ?.from('user_profiles')
        ?.select('id')
        ?.eq('company_id', companyId)
        ?.eq('role', 'admin');
      if (!admins?.length) return;
      const certLabel = certType?.replace(/_/g, ' ')?.replace(/\b\w/g, (c) => c?.toUpperCase()) || certType;
      await Promise.all(
        admins?.map((a) =>
          this.createNotification(
            a?.id,
            'cert_expired',
            'Certification Expired',
            `A worker's ${certLabel} certification has expired. Immediate renewal required.`,
            `/worker-certifications`,
            workerUserId,
            null
          )
        )
      );
    } catch (err) {
      console.error('notifyCertExpired error:', err?.message);
    }
  },

  /**
   * Notify all admin users in a company that a document is expiring soon.
   */
  async notifyDocumentExpiringSoon(documentId, documentName, expiryDate, companyId, siteId) {
    try {
      if (!companyId) return;
      const { data: admins } = await supabase
        ?.from('user_profiles')
        ?.select('id')
        ?.eq('company_id', companyId)
        ?.eq('role', 'admin');
      if (!admins?.length) return;
      const expiryStr = expiryDate ? new Date(expiryDate)?.toLocaleDateString('en-GB') : 'soon';
      await Promise.all(
        admins?.map((a) =>
          this.createNotification(
            a?.id,
            'document_expiring',
            'Document Expiring Soon',
            `"${documentName || 'A document'}" is expiring on ${expiryStr}. Please review or renew it.`,
            `/document-management`,
            documentId,
            siteId || null
          )
        )
      );
    } catch (err) {
      console.error('notifyDocumentExpiringSoon error:', err?.message);
    }
  },

  /**
   * Notify all admin users in a company that a new issue (snag) has been logged.
   */
  async notifyNewIssueLogged(snagId, snagTitle, companyId, siteId, reportedByName) {
    try {
      if (!companyId) return;
      const { data: admins } = await supabase
        ?.from('user_profiles')
        ?.select('id')
        ?.eq('company_id', companyId)
        ?.eq('role', 'admin');
      if (!admins?.length) return;
      await Promise.all(
        admins?.map((a) =>
          this.createNotification(
            a?.id,
            'snag_raised',
            'New Issue Logged',
            `${reportedByName || 'A team member'} logged a new issue: "${snagTitle || 'Untitled issue'}".`,
            siteId ? `/sites/${siteId}?tab=snags` : `/snags`,
            snagId,
            siteId || null
          )
        )
      );
    } catch (err) {
      console.error('notifyNewIssueLogged error:', err?.message);
    }
  },

  /**
   * Notify all admin users in a company that an issue (snag) has been closed/resolved.
   */
  async notifyIssueClosed(snagId, snagTitle, companyId, siteId, resolvedByName) {
    try {
      if (!companyId) return;
      const { data: admins } = await supabase
        ?.from('user_profiles')
        ?.select('id')
        ?.eq('company_id', companyId)
        ?.eq('role', 'admin');
      if (!admins?.length) return;
      await Promise.all(
        admins?.map((a) =>
          this.createNotification(
            a?.id,
            'success',
            'Issue Closed',
            `${resolvedByName || 'A team member'} resolved the issue: "${snagTitle || 'Untitled issue'}".`,
            siteId ? `/sites/${siteId}?tab=snags` : `/snags`,
            snagId,
            siteId || null
          )
        )
      );
    } catch (err) {
      console.error('notifyIssueClosed error:', err?.message);
    }
  },

  /**
   * Notify all admin users in a company that a new user has joined.
   */
  async notifyUserJoined(newUserId, newUserName, companyId) {
    try {
      if (!companyId) return;
      const { data: admins } = await supabase
        ?.from('user_profiles')
        ?.select('id')
        ?.eq('company_id', companyId)
        ?.eq('role', 'admin');
      if (!admins?.length) return;
      await Promise.all(
        admins?.map((a) => {
          if (a?.id === newUserId) return Promise.resolve(); // don't notify the user themselves
          return this.createNotification(
            a?.id,
            'success',
            'New User Joined',
            `${newUserName || 'A new user'} has joined your company.`,
            `/invite-users`,
            newUserId,
            null
          );
        })
      );
    } catch (err) {
      console.error('notifyUserJoined error:', err?.message);
    }
  },

  /**
   * Notify all admin users in a company that a site report has been exported.
   */
  async notifyReportExported(companyId, siteId, siteName, exportedByName) {
    try {
      if (!companyId) return;
      const { data: admins } = await supabase
        ?.from('user_profiles')
        ?.select('id')
        ?.eq('company_id', companyId)
        ?.eq('role', 'admin');
      if (!admins?.length) return;
      await Promise.all(
        admins?.map((a) =>
          this.createNotification(
            a?.id,
            'success',
            'Site Report Exported',
            `${exportedByName || 'A team member'} exported a report for "${siteName || 'a site'}".`,
            `/report-builder`,
            siteId || null,
            siteId || null
          )
        )
      );
    } catch (err) {
      console.error('notifyReportExported error:', err?.message);
    }
  },
};
